// @ts-check

import { cloneDeep } from "lodash";
import { mergeWithResolvers } from "src/util/mergeWithResolver";
import Consts from "./consts";

/**
 * @param {user_prefs.gql.UserPreferences[]} prefs
 * @returns {user_prefs.PreferencesByScope}
 */
const getScopedPrefsFromGqlPrefs = (prefs) => {
  const find = (/** @type {user_prefs.Scope} */ scope) => {
    return prefs.find((p) => p.scope === scope)?.preferences || {};
  };

  return {
    [Consts.Scopes.GLOBAL_ALL_USERS]: find(Consts.Scopes.GLOBAL_ALL_USERS),
    [Consts.Scopes.GLOBAL_USER]: find(Consts.Scopes.GLOBAL_USER),
    [Consts.Scopes.WORKSPACE_ALL_USERS]: find(
      Consts.Scopes.WORKSPACE_ALL_USERS
    ),
    [Consts.Scopes.WORKSPACE_USER]: find(Consts.Scopes.WORKSPACE_USER),
  };
};

/**
 * @param {user_prefs.gql.UserPreferences[]} prefs
 * @returns {user_prefs.Preferences}
 */
const getMergedPrefsFromGqlPrefs = (prefs) => {
  const prefsByScope = getScopedPrefsFromGqlPrefs(prefs);

  return mergeWithResolvers(
    [
      {
        path: "pinnedItems",
        resolver: () =>
          prefsByScope[Consts.Scopes.WORKSPACE_USER]?.pinnedItems ||
          Consts.defaultPreferences.pinnedItems,
      },
      {
        path: "sideNav",
        resolver: () =>
          prefsByScope[Consts.Scopes.GLOBAL_USER]?.sideNav ||
          Consts.defaultPreferences.sideNav,
      },
    ],
    Consts.defaultPreferences,
    ...Object.values(prefsByScope)
  );
};

/**
 * @param {user_prefs.PreferencesByScope} prefs
 * @returns {user_prefs.gql.UserPreferencesUpdate[]}
 */
const getGqlUpdatesFromPrefsCollection = (prefs) => {
  /** @type {user_prefs.gql.UserPreferencesUpdate[]} */
  const updates = [];

  const addToUpdates = (/** @type {user_prefs.Scope} */ scope) => {
    const pref = prefs[scope];
    if (pref) {
      updates.push({
        scope,
        preferences: pref,
      });
    }
  };

  addToUpdates(Consts.Scopes.GLOBAL_ALL_USERS);
  addToUpdates(Consts.Scopes.GLOBAL_USER);
  addToUpdates(Consts.Scopes.WORKSPACE_ALL_USERS);
  addToUpdates(Consts.Scopes.WORKSPACE_USER);

  return updates;
};

/**
 * @param {user_prefs.gql.UserPreferences[]} cachedPrefs
 * @param {user_prefs.gql.UserPreferences[]} updatedPrefs
 * @returns {any}
 */
const getPrefsForCacheWrite = (cachedPrefs, updatedPrefs) => {
  const newPrefs = cloneDeep(cachedPrefs);

  for (const updatedPref of updatedPrefs) {
    const pref = newPrefs.find((p) => p.scope === updatedPref.scope);
    if (pref) {
      pref.preferences = updatedPref.preferences;
    } else {
      newPrefs.push(updatedPref);
    }
  }

  return newPrefs;
};

export default {
  getScopedPrefsFromGqlPrefs,
  getMergedPrefsFromGqlPrefs,
  getGqlUpdatesFromPrefsCollection,
  getPrefsForCacheWrite,
};
